import { createApi } from '@reduxjs/toolkit/query/react';
import EventDTO, {
  CreateEventAudienceDiscountDTO, EventAssistanceReportDTO, EventBalanceDTO, EventCashierReportDTO, EventAudienceDiscountDTO, SellerEventQR,
  SellerReferralsReportDTO,
  EventCashRegisterDTO,
  CreateEventCashRegisterDTO,
  UpdateEventCashRegisterDTO,
  EventCashRegisterMovementDTO,
  CreateEventCashRegisterMovementDTO,
  OpenCashRegisterResponseDTO,
  EventSubscriptionDTO,
  CreateEventSubscriptionDTO,
  CategoryDTO,
  EventCategoryDTO,
  EventSummaryDTO,
  EventStatsDTO,
} from '../../models/event';
import { Response } from '../../models/shared';
import { AgentDTO } from '../../models/agent';
import baseQueryWithReauth from './base';
import {
  QuestionDTO,
  PollResponse,
} from '../../models/poll';
import { DisplaySeatDTO } from '../../models/seat';
import UserDTO, { UserEvent } from '../../models/user';
import { CreateEventTransactionDTO, EventTransactionDTO } from '../../models/event-transaction';
// eslint-disable-next-line import/no-cycle
import { ordersApi } from './orders';
// eslint-disable-next-line import/no-cycle
import { businessesApi } from './business';
import { CreateWireTransferDTO, WireTransferDTO } from '../../models/wire-transfer';
import { EventSeatDTO } from '../../models/event-seat';
import { referralsApi } from './referrals';
import { CreateShowtimeDTO, ShowtimeDTO } from '../../models/showtime';

export interface ItemExportRow {
  id: string;
  order_display_id: string;
  item_ticket_id: string;
  name: string;
  last_name: string;
  qr_code: string;
}

const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['events', 'events_users', 'businesses_users', 'businesses', 'events_transactions', 'event_seats', 'seller_summary', 'orders', 'cashier_report', 'event_assistance_report', 'event_sellers_report', 'event_audience_discounts', 'event_cash_registers', 'event_cash_movements', 'showtimes', 'event_categories'],
  endpoints: (build) => ({
    getEventById: build.query<EventDTO | null, { eventId: string, slug?: boolean }>({
      providesTags: (result, error, { eventId }) => [{ type: 'events', id: eventId }, { type: 'events' }],
      query: ({ eventId, slug }) => ({
        url: slug ? `/events/slug/${eventId}` : `/events/${eventId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO>): (EventDTO | null) => response.data || null,
    }),
    getEventBySlug: build.query<EventDTO | null, { slug: string }>({
      providesTags: (result, error, { slug }) => [{ type: 'events', id: slug }, { type: 'events' }],
      query: ({ slug }) => ({
        url: `/events/slug/${slug}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO>): (EventDTO | null) => response.data || null,
    }),
    getAllEvents: build.query<EventDTO[] | null, { query?: Partial<EventDTO> }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ query }) => ({
        url: '/events/all/available',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO[]>): (EventDTO[] | null) => response.data || null,
    }),
    getEventsByQuery: build.query<EventDTO[] | null, { query?: Partial<EventDTO> }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ query }) => ({
        url: '/events/',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO[]>): (EventDTO[] | null) => response.data || null,
    }),
    getAllBusinessEvents: build.query<Response<EventDTO[]>, number>({
      providesTags: () => [{ type: 'events' }],
      query: (business_id) => ({
        url: `/businesses/${business_id}/events`,
        method: 'GET',
      }),
    }),
    getBusinessEvents: build.query<Response<EventDTO[]>, { businessId: string, onlyActive?: boolean }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, onlyActive }) => ({
        url: `/businesses/${businessId}/events`,
        method: 'GET',
        params: {
          active: onlyActive,
        },
      }),
    }),
    getCurrentAgent: build.query<AgentDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/agents`,
        method: 'GET',
        params: {
          now: 'true',
        },
      }),
      transformResponse: (response: Response<AgentDTO>): (AgentDTO | null) => response.data || null,
    }),
    createEvent: build.mutation<Response<EventDTO>, { event: Partial<EventDTO>, businessId: string }>({
      query: ({
        event, businessId,
      }) => ({
        url: `/businesses/${businessId}/events/`,
        method: 'POST',
        body: event,
      }),
      invalidatesTags: ['events'],
    }),
    updateEvent: build.mutation<Response<EventDTO>, { event: Partial<EventDTO>, businessId: string }>({
      query: ({
        event, businessId,
      }) => ({
        url: `/businesses/${businessId}/events/`,
        method: 'PUT',
        body: event,
      }),
      invalidatesTags: ['events'],
    }),
    getEventQuestions: build.query<QuestionDTO[] | null, { eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ eventId }) => ({
        url: `/events/${eventId}/polls/questions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<QuestionDTO[]>): (QuestionDTO[] | null) => response.data || null,
    }),
    getSeatsInfo: build.query<DisplaySeatDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: () => [{ type: 'event_seats' }],
      query: ({ eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats?display=true`,
        method: 'GET',
      }),
      transformResponse: (response: Response<DisplaySeatDTO[]>): (DisplaySeatDTO[] | null) => response.data || null,
    }),
    getEventUsers: build.query<UserDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: () => [{ type: 'events_users' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO[]>): (UserDTO[] | null) => response.data || null,
    }),
    addUserToEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, userData: { userId: string, comissionRate: number, active: boolean, permissions: string, seller_group_id?: string, scanner_type_id?: string | null } }>({
      query: ({
        businessId, eventId, userData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'POST',
        body: {
          user_id: userData.userId,
          seller_comission_rate: userData.comissionRate,
          permissions: userData.permissions,
          active: userData.active,
          seller_group_id: userData.seller_group_id,
          scanner_type_id: userData.scanner_type_id,
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'seller_summary', 'orders', 'businesses'],
    }),
    updateUserEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, data: Partial<UserEvent> & { id: string } }>({
      query: ({
        businessId, eventId, data,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'seller_summary'],
    }),
    deleteUserEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, userEventId: string }>({
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(referralsApi.util.invalidateTags(['referrals']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'businesses'],
    }),
    addEventTransaction: build.mutation<Response<CreateEventTransactionDTO>, { businessId: string, eventId: string, transactionData: CreateEventTransactionDTO }>({
      query: ({
        businessId, eventId, transactionData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions`,
        method: 'POST',
        body: transactionData,
      }),
      invalidatesTags: ['events_transactions', 'cashier_report'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    updateEventTransaction: build.mutation<Response<EventTransactionDTO>, { businessId: string, eventId: string, transactionId: string, transactionData: Partial<EventTransactionDTO> }>({
      query: ({
        businessId, eventId, transactionId, transactionData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions/${transactionId}`,
        method: 'PUT',
        body: transactionData,
      }),
      invalidatesTags: ['events_transactions'],
    }),
    deleteEventTransaction: build.mutation<Response<EventTransactionDTO>, { businessId: string, eventId: string, transactionId: string }>({
      query: ({ businessId, eventId, transactionId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions/${transactionId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_transactions'],
    }),
    getEventTransactions: build.query<Response<EventTransactionDTO[]>, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events_transactions' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions`,
        method: 'GET',
      }),
    }),
    getEventCapacity: build.query<{ used_tickets: number } | null, { eventId: string, businessId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/capacity`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ used_tickets: number }>): ({ used_tickets: number } | null) => response.data || null,
    }),
    countSellerQrCode: build.mutation<Response<null>, { businessId: string, eventId: string, userEventId: string }>({
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}/qr/count`,
        method: 'POST',
      }),
    }),
    getSellerEventQr: build.query<SellerEventQR | null, { businessId: string, eventId: string, userEventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}/qr`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerEventQR>): (SellerEventQR | null) => response.data || null,
    }),
    getEventBalance: build.query<EventBalanceDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/balance`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventBalanceDTO>): (EventBalanceDTO | null) => response.data || null,
    }),
    createWireTransfer: build.mutation<Response<WireTransferDTO>, { wire: CreateWireTransferDTO, businessId: string, eventId: string }>({
      query: ({ wire, businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/wires`,
        method: 'POST',
        body: wire,
      }),
      invalidatesTags: ['events'],
    }),
    importSellersToEvent: build.mutation<Response<null>, { businessId: string, eventId: string, eventIdToImport: string }>({
      query: ({ businessId, eventId, eventIdToImport }) => ({
        url: `/businesses/${businessId}/events/${eventId}/import-users`,
        method: 'POST',
        body: {
          import_from: eventIdToImport,
        },
      }),
      invalidatesTags: ['events_users', 'seller_summary'],
    }),
    reserveSeat: build.mutation<Response<EventSeatDTO>, { reservationCode: string, businessId: string, eventId: string, eventSeatId: string }>({
      query: ({
        reservationCode, businessId, eventId, eventSeatId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/${eventSeatId}/reserve`,
        method: 'POST',
        body: { code: reservationCode },
      }),
      invalidatesTags: ['event_seats'],
    }),
    freeSeat: build.mutation<Response<EventSeatDTO>, { reservationCode: string, businessId: string, eventId: string, eventSeatId: string }>({
      query: ({
        reservationCode, businessId, eventId, eventSeatId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/${eventSeatId}/free`,
        method: 'POST',
        body: { code: reservationCode },
      }),
      invalidatesTags: ['event_seats'],
    }),
    getEventCashierReport: build.query<EventCashierReportDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'cashier_report' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cashier-report`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventCashierReportDTO[]>): (EventCashierReportDTO[] | null) => response.data || null,
    }),
    getOneCashierReport: build.query<EventCashierReportDTO | null, { businessId: string, eventId: string, cashierId: string }>({
      providesTags: () => [{ type: 'cashier_report' }],
      query: ({ businessId, eventId, cashierId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cashier-report/${cashierId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventCashierReportDTO>): (EventCashierReportDTO | null) => response.data || null,
    }),
    getEventAssistanceReport: build.query<EventAssistanceReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_assistance_report' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/assistance`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventAssistanceReportDTO>): (EventAssistanceReportDTO | null) => response.data || null,
    }),
    getEventSellersReport: build.query<SellerReferralsReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_sellers_report' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/referrals-sellers`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerReferralsReportDTO>): (SellerReferralsReportDTO | null) => response.data || null,
    }),
    createAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountData: CreateEventAudienceDiscountDTO }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({ businessId, eventId, discountData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'POST',
        body: discountData,
      }),
    }),
    getAudienceDiscounts: build.query<Response<EventAudienceDiscountDTO[]>, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_audience_discounts' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'GET',
      }),
    }),
    updateAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountId: string, discountData: Partial<EventAudienceDiscountDTO> }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({
        businessId, eventId, discountId, discountData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'PUT',
        body: {
          ...discountData,
          id: discountId,
        },
      }),
    }),
    deleteAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountId: string }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({ businessId, eventId, discountId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts/${discountId}`,
        method: 'DELETE',
      }),
    }),
    createEventCashRegister: build.mutation<Response<EventCashRegisterDTO>, { businessId: string, eventId: string, cashRegisterData: CreateEventCashRegisterDTO }>({
      invalidatesTags: ['event_cash_registers'],
      query: ({ businessId, eventId, cashRegisterData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers`,
        method: 'POST',
        body: cashRegisterData,
      }),
    }),
    getEventCashRegisters: build.query<EventCashRegisterDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_cash_registers' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventCashRegisterDTO[]>): (EventCashRegisterDTO[] | null) => response.data || null,
    }),
    openEventCashRegister: build.mutation<Response<OpenCashRegisterResponseDTO>, { businessId: string, eventId: string, cashRegisterId: string, initialBalance: number }>({
      query: ({
        businessId, eventId, cashRegisterId, initialBalance,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}/open`,
        method: 'POST',
        body: {
          initial_balance: initialBalance,
        },
      }),
      invalidatesTags: ['event_cash_registers'],
    }),
    closeEventCashRegister: build.mutation<Response<EventCashRegisterDTO>, { businessId: string, eventId: string, cashRegisterId: string, closingBalance: number }>({
      query: ({
        businessId, eventId, cashRegisterId, closingBalance,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}/close`,
        method: 'POST',
        body: {
          closing_balance: closingBalance,
        },

      }),
      invalidatesTags: ['event_cash_registers'],
    }),
    updateEventCashRegister: build.mutation<Response<EventCashRegisterDTO>, { businessId: string, eventId: string, cashRegisterId: string, cashRegisterData: UpdateEventCashRegisterDTO }>({
      query: ({
        businessId, eventId, cashRegisterId, cashRegisterData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}`,
        method: 'PUT',
        body: cashRegisterData,
      }),
      invalidatesTags: ['event_cash_registers'],
    }),
    deleteEventCashRegister: build.mutation<Response<EventCashRegisterDTO>, { businessId: string, eventId: string, cashRegisterId: string }>({
      query: ({ businessId, eventId, cashRegisterId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['event_cash_registers'],
    }),
    getEventCashRegisterById: build.query<EventCashRegisterDTO | null, { businessId: string, eventId: string, cashRegisterId: string }>({
      query: ({ businessId, eventId, cashRegisterId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventCashRegisterDTO>): (EventCashRegisterDTO | null) => response.data || null,
    }),
    getEventCashRegisterMovements: build.query<EventCashRegisterMovementDTO[] | null, { businessId: string, eventId: string, cashRegisterId: string, sessionId: string }>({
      query: ({
        businessId, eventId, cashRegisterId, sessionId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}/sessions/${sessionId}/movements`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventCashRegisterMovementDTO[]>): (EventCashRegisterMovementDTO[] | null) => response.data || null,
      providesTags: () => ['event_cash_movements'],
    }),
    createEventCashRegisterMovement: build.mutation<Response<EventCashRegisterMovementDTO>, { businessId: string, eventId: string, cashRegisterId: string, sessionId: string, movementData: CreateEventCashRegisterMovementDTO }>({
      query: ({
        businessId, eventId, cashRegisterId, sessionId, movementData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/cash-registers/${cashRegisterId}/sessions/${sessionId}/movements`,
        method: 'POST',
        body: movementData,
      }),
      invalidatesTags: ['event_cash_movements'],
    }),
    createShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeData: CreateShowtimeDTO }>({
      query: ({ businessId, eventId, showtimeData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes`,
        method: 'POST',
        body: showtimeData,
      }),
      invalidatesTags: ['showtimes'],
    }),
    updateShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeId: string, showtimeData: Partial<ShowtimeDTO> }>({
      query: ({
        businessId, eventId, showtimeId, showtimeData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes/${showtimeId}`,
        method: 'PUT',
        body: showtimeData,
      }),
      invalidatesTags: ['showtimes'],
    }),
    deleteShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeId: string }>({
      query: ({ businessId, eventId, showtimeId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes/${showtimeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['showtimes'],
    }),
    getShowtimes: build.query<ShowtimeDTO[] | null, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes`,
        method: 'GET',
      }),
      providesTags: ['showtimes'],
      transformResponse: (response: Response<ShowtimeDTO[]>): (ShowtimeDTO[] | null) => response.data || null,
    }),
    getEventSubscription: build.query<EventSubscriptionDTO[] | null, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSubscriptionDTO[]>): (EventSubscriptionDTO[] | null) => response.data || null,
    }),
    createEventSubscription: build.mutation<Response<EventSubscriptionDTO>, { businessId: string, eventId: string, subscriptionData: CreateEventSubscriptionDTO }>({
      query: ({ businessId, eventId, subscriptionData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions`,
        method: 'POST',
        body: subscriptionData,
      }),
      invalidatesTags: ['events'],
    }),
    deleteEventBySuscribedId: build.mutation<Response<EventSubscriptionDTO>, { businessId: string, eventId: string, suscribedId: string }>({
      query: ({ businessId, eventId, suscribedId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions/by-event/${suscribedId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['events'],
    }),
    cloneEvent: build.mutation<Response<{ event: EventDTO }>, { businessId: string, eventId: string, name: string }>({
      query: ({ businessId, eventId, name }) => ({
        url: `/businesses/${businessId}/events/${eventId}/clone`,
        method: 'POST',
        body: {
          name,
        },
      }),
      invalidatesTags: ['events'],
    }),
    getCategories: build.query<Response<CategoryDTO[]>, void>({
      query: () => ({
        url: '/categories',
        method: 'GET',
      }),
    }),
    getCategoriesByEventId: build.query<Response<EventCategoryDTO[]>, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/events/${eventId}/categories`,
        method: 'GET',
      }),
      providesTags: ['event_categories'],
    }),
    createEventCategory: build.mutation<Response<EventCategoryDTO>, { eventId: string, categoryId: string }>({
      query: ({ eventId, categoryId }) => ({
        url: `/events/${eventId}/categories`,
        method: 'POST',
        body: { category_id: categoryId },
      }),
      invalidatesTags: ['event_categories'],
    }),
    deleteEventCategory: build.mutation<Response<void>, { eventId: string, eventCategoryId: string }>({
      query: ({ eventId, eventCategoryId }) => ({
        url: `/events/${eventId}/categories/${eventCategoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['event_categories'],
    }),
    getEventResponsesTable: build.query<PollResponse[] | null, { eventId: string, businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/responses-table`,
        method: 'GET',
      }),
    }),
    getEventResponsesByOrderId: build.mutation<PollResponse[] | null, { eventId: string, businessId: string, orderId: string }>({
      query: ({ eventId, businessId, orderId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/responses-by-order-id/${orderId}`,
        method: 'GET',
      }),
    }),
    getEventsSummary: build.query<EventSummaryDTO[] | null, void>({
      query: () => ({
        url: '/events/all/summary',
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSummaryDTO[]>): (EventSummaryDTO[] | null) => response.data || null,
    }),
    getEventsSummaryXlsx: build.mutation<void, void>({
      query: () => ({
        url: '/events/all/summary/xlsx',
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `summary-events-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventStats: build.query<EventStatsDTO | null, string | undefined>({
      providesTags: () => [{ type: 'events' }],
      query: (user_id) => ({
        url: `/events/all/stats/${user_id || ''}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventStatsDTO>): (EventStatsDTO | null) => response.data || null,
    }),
    getEventSummaryPDF: build.mutation<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/summary-pdf`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}. ${errorText}`);
          }
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorText = await response.text();
            throw new Error(`Server returned JSON instead of PDF: ${errorText.substring(0, 200)}`);
          }
          const blob = await response.blob();
          if (blob.size === 0) {
            throw new Error('Received empty PDF file');
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `summary-events-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventItemsExport: build.query<ItemExportRow[] | null, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/events/${eventId}/items/export`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ItemExportRow[]>): (ItemExportRow[] | null) => response.data || null,
    }),
  }),
});

export const {
  useGetEventByIdQuery,
  useGetAllBusinessEventsQuery,
  useGetBusinessEventsQuery,
  useGetCurrentAgentQuery,
  useGetAllEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useGetEventQuestionsQuery,
  useGetSeatsInfoQuery,
  useGetEventUsersQuery,
  useAddUserToEventMutation,
  useDeleteUserEventMutation,
  useAddEventTransactionMutation,
  useDeleteEventTransactionMutation,
  useUpdateEventTransactionMutation,
  useGetEventTransactionsQuery,
  useGetEventCapacityQuery,
  useCountSellerQrCodeMutation,
  useGetSellerEventQrQuery,
  useLazyGetSellerEventQrQuery,
  useUpdateUserEventMutation,
  useGetEventBalanceQuery,
  useCreateWireTransferMutation,
  useImportSellersToEventMutation,
  useReserveSeatMutation,
  useFreeSeatMutation,
  useGetEventCashierReportQuery,
  useGetOneCashierReportQuery,
  useGetEventAssistanceReportQuery,
  useGetEventSellersReportQuery,
  useGetEventsByQueryQuery,
  useCreateAudienceDiscountMutation,
  useGetAudienceDiscountsQuery,
  useUpdateAudienceDiscountMutation,
  useDeleteAudienceDiscountMutation,
  useCreateEventCashRegisterMutation,
  useGetEventCashRegistersQuery,
  useOpenEventCashRegisterMutation,
  useCloseEventCashRegisterMutation,
  useUpdateEventCashRegisterMutation,
  useDeleteEventCashRegisterMutation,
  useGetEventCashRegisterByIdQuery,
  useGetEventCashRegisterMovementsQuery,
  useCreateEventCashRegisterMovementMutation,
  useCreateShowtimeMutation,
  useUpdateShowtimeMutation,
  useDeleteShowtimeMutation,
  useGetShowtimesQuery,
  useGetEventSubscriptionQuery,
  useCreateEventSubscriptionMutation,
  useDeleteEventBySuscribedIdMutation,
  useGetCategoriesByEventIdQuery,
  useGetCategoriesQuery,
  useCreateEventCategoryMutation,
  useDeleteEventCategoryMutation,
  useGetEventBySlugQuery,
  useCloneEventMutation,
  useGetEventResponsesByOrderIdMutation,
  useGetEventResponsesTableQuery,
  useGetEventsSummaryQuery,
  useGetEventsSummaryXlsxMutation,
  useGetEventStatsQuery,
  useGetEventSummaryPDFMutation,
  useGetEventItemsExportQuery,
} = eventsApi;
export { eventsApi };
